import React, { useEffect, useReducer } from 'react';
import { Grid } from 'semantic-ui-react';

import GridColumnInputWrapper from '../../../../common/GridColumnInputWrapper';
import { AccordionWrapper, InputWrapper } from 'react-lib/dist/components-es';
import { FILE_EXTENSION_PNG_IMAGE, PPE_ICONS_PATH } from '../../../../../constants';
import CommonSelectInputWrapper from '../../../../common/CommonSelectInputWrapper';
import { getOptions } from '../../../../../utils';
import { useTranslation } from 'react-i18next';

let maskRespiratorOptions: any[] = [];
let glovesOptions: any[] = [];

type Props = {
  initialData: any;
  values: any;
  disabled: boolean;
  setFieldValue: (arg1: string, arg2: string) => void;
  errors: any;
};

const IPPEInformationContainer = ({
  initialData,
  values,
  disabled,
  setFieldValue,
  errors,
}: Props) => {
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const { i18n } = useTranslation();

  useEffect(() => {
    initOptions();
  }, [(i18n as any).language]);

  const initOptions = () => {
    const { ppeMaskRespiratorList, ppeGloveList } = initialData;
    maskRespiratorOptions = getOptions(
      ppeMaskRespiratorList,
      values.chemicalRiskAttributes.ppeMaskRespiratorRef,
      ['name']
    );
    maskRespiratorOptions.unshift({ text: 'Select', value: null, key: -1 });

    glovesOptions = getOptions(ppeGloveList, values.chemicalRiskAttributes.ppeGlovesRef, ['name']);
    glovesOptions.unshift({ text: 'Select', value: null, key: -1 });
    forceUpdate();
  };

  const getPPEIcon = (iconName: string) => {
    return (
      <img
        className="left-floated"
        src={window.location.origin + PPE_ICONS_PATH + iconName + FILE_EXTENSION_PNG_IMAGE}
        height="30"
      />
    );
  };

  const getGridColumnCheckboxAndPPEIcon = (
    checkboxPropName: string,
    checkBoxlabel: string,
    iconName: string,
    onChange = () => {}
  ) => {
    return (
      <GridColumnInputWrapper
        fieldClassName="checkboxInline checkboxMargin"
        value={values.chemicalRiskAttributes[checkboxPropName] || false}
        name={'chemicalRiskAttributes.' + checkboxPropName}
        label={checkBoxlabel}
        onChange={onChange}
        setFieldValue={setFieldValue}
        errors={errors}
        values={values}
        displayLabelAfter={true}
        labelClassName="checkboxLabelOnRight"
        render={(props: any) => getInputAndPPEIcon(props, iconName)}
      />
    );
  };

  const getInputAndPPEIcon = (props: any, iconName: string) => {
    return (
      <>
        <span className="ppeIcon">{getPPEIcon(iconName)}</span>
        <InputWrapper type="checkbox" inputClassName="left-floated" {...props} />
      </>
    );
  };

  const onChangeClearRefId = (refToClear: string, refToCheck: string) => {
    if (values.chemicalRiskAttributes[refToCheck]) values.chemicalRiskAttributes[refToClear] = {};
    else values.chemicalRiskAttributes[refToClear] = null;
    forceUpdate();
  };

  const getContainerContent = () => {
    return (
      <Grid columns={3} verticalAlign="bottom">
        {getGridColumnCheckboxAndPPEIcon(
          'ppeIsEyeProtectionRequired',
          'inventory.details.page.ppe.inf.container.is.eye.protection.required',
          'eyeProtection'
        )}
        {getGridColumnCheckboxAndPPEIcon(
          'ppeIsMaskRespiratorRequired',
          'inventory.details.page.ppe.inf.container.is.mask.respirator.required',
          'maskRespirator',
          () => onChangeClearRefId('ppeMaskRespiratorRef', 'ppeIsMaskRespiratorRequired')
        )}
        <GridColumnInputWrapper
          value={
            values.chemicalRiskAttributes.ppeMaskRespiratorRef
              ? values.chemicalRiskAttributes.ppeMaskRespiratorRef.id
              : null
          }
          name="chemicalRiskAttributes.ppeMaskRespiratorRef.id"
          disabled={disabled || !values.chemicalRiskAttributes.ppeIsMaskRespiratorRequired}
          options={maskRespiratorOptions}
          placeholder="select.input.default.placeholder"
          propUsedInChanges="name"
          setFieldValue={setFieldValue}
          errors={errors}
          values={values}
          specialRequiredCondition={
            values.chemicalRiskAttributes.ppeIsMaskRespiratorRequired == true
          }
          render={(props: any) => <CommonSelectInputWrapper {...props} />}
        />
        {getGridColumnCheckboxAndPPEIcon(
          'ppeIsEyeAndFaceProtectionRequired',
          'inventory.details.page.ppm.inf.container.is.eye.and.face.protection.required',
          'eyeAndFaceProtection'
        )}
        {getGridColumnCheckboxAndPPEIcon(
          'ppeIsGlovesRequired',
          'inventory.details.page.ppe.inf.container.is.gloves.required',
          'gloves',
          () => onChangeClearRefId('ppeGlovesRef', 'ppeIsGlovesRequired')
        )}
        <GridColumnInputWrapper
          value={
            values.chemicalRiskAttributes.ppeGlovesRef
              ? values.chemicalRiskAttributes.ppeGlovesRef.id
              : null
          }
          name="chemicalRiskAttributes.ppeGlovesRef.id"
          disabled={disabled || !values.chemicalRiskAttributes.ppeIsGlovesRequired}
          options={glovesOptions}
          setFieldValue={setFieldValue}
          errors={errors}
          values={values}
          placeholder="select.input.default.placeholder"
          specialRequiredCondition={values.chemicalRiskAttributes.ppeIsGlovesRequired == true}
          propUsedInChanges="name"
          render={(props: any) => <CommonSelectInputWrapper {...props} />}
        />
        {getGridColumnCheckboxAndPPEIcon(
          'ppeIsOverallsClothingRequired',
          'inventory.details.page.ppm.inf.container.is.overalls.clothing.required',
          'overallsClothing'
        )}

        {getGridColumnCheckboxAndPPEIcon(
          'ppeIsOtherRequired',
          'inventory.details.page.ppe.inf.container.is.other.required',
          'other',
          () => setFieldValue('chemicalRiskAttributes.ppeOtherRef', '')
        )}
        <GridColumnInputWrapper
          name="chemicalRiskAttributes.ppeOtherRef"
          value={values.chemicalRiskAttributes.ppeOtherRef || ''}
          setFieldValue={setFieldValue}
          errors={errors}
          values={values}
          disabled={disabled || !values.chemicalRiskAttributes.ppeIsOtherRequired}
          render={(props: any) => <InputWrapper {...props} type="text" />}
        />
      </Grid>
    );
  };

  return (
    <AccordionWrapper
      accordionTitle="inventory.details.page.ppe.inf.container.title"
      className="containerReducedWhiteSpace"
      render={getContainerContent}
    />
  );
};

export default IPPEInformationContainer;
